import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

interface CustomDomainTabProps {
  config: IBrandingConfig | null;
  onAddDomain: (domain: string) => Promise<void>;
  onVerifyDomain: () => Promise<void>;
  onDeleteDomain: () => Promise<void>;
  saving: boolean;
}

export const CustomDomainTab: React.FC<CustomDomainTabProps> = ({
  config,
  onAddDomain,
  onVerifyDomain,
  onDeleteDomain,
  saving,
}) => {
  const { t } = useTranslation("common");
  const [domainInput, setDomainInput] = useState("");

  const customDomain = config?.customDomain;
  const hasDomain = !!customDomain?.domain;

  const handleAddDomain = async () => {
    if (!domainInput.trim()) return;
    await onAddDomain(domainInput.trim());
    setDomainInput("");
  };

  const getStatusColor = (verified: boolean) => {
    return verified ? "green" : "yellow";
  };

  const getStatusLabel = (verified: boolean) => {
    return verified
      ? t("admin.branding.domain.verified")
      : t("admin.branding.domain.pending");
  };

  return (
    <Box p="16px">
      <Text fontWeight={600} fontSize="18px" mb="16px">
        {t("admin.branding.domain.title")}
      </Text>

      <Flex flexDir="column" gap="24px">
        {/* Add Domain */}
        {!hasDomain && (
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            p="24px"
          >
            <FormControl maxW="500px">
              <FormLabel fontSize="14px" fontWeight={500}>
                {t("admin.branding.domain.addDomain")}
              </FormLabel>
              <Flex gap="12px">
                <Input
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  placeholder={t("admin.branding.domain.placeholder")}
                  borderRadius="8px"
                />
                <Button
                  colorScheme="blue"
                  onClick={handleAddDomain}
                  isLoading={saving}
                  minW="120px"
                >
                  {t("admin.branding.domain.addDomain")}
                </Button>
              </Flex>
            </FormControl>
          </Box>
        )}

        {/* Domain Status */}
        {hasDomain && (
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            p="24px"
          >
            <Flex justify="space-between" align="center" mb="16px">
              <Flex align="center" gap="12px">
                <Text fontWeight={600} fontSize="16px">
                  {customDomain.domain}
                </Text>
                <Badge colorScheme={getStatusColor(customDomain.verified)}>
                  {getStatusLabel(customDomain.verified)}
                </Badge>
                {customDomain.sslEnabled && (
                  <Badge colorScheme="green">
                    {t("admin.branding.domain.sslActive")}
                  </Badge>
                )}
              </Flex>
              <Flex gap="8px">
                {!customDomain.verified && (
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={onVerifyDomain}
                    isLoading={saving}
                  >
                    {t("admin.branding.domain.verify")}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  onClick={onDeleteDomain}
                  isLoading={saving}
                >
                  {t("admin.branding.domain.remove")}
                </Button>
              </Flex>
            </Flex>

            {/* DNS Records */}
            {customDomain.dnsRecords && customDomain.dnsRecords.length > 0 && (
              <Box>
                <Text fontWeight={500} fontSize="14px" mb="8px">
                  {t("admin.branding.domain.dnsConfig")}
                </Text>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Type</Th>
                      <Th>Name</Th>
                      <Th>Value</Th>
                      <Th>{t("admin.branding.domain.status")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {customDomain.dnsRecords.map(
                      (record: IBrandingDnsRecord, index: number) => (
                        <Tr key={index}>
                          <Td>{record.type}</Td>
                          <Td>
                            <Text fontSize="12px" fontFamily="mono">
                              {record.name}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="12px" fontFamily="mono">
                              {record.value}
                            </Text>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={record.verified ? "green" : "yellow"}
                            >
                              {record.verified
                                ? t("admin.branding.domain.verified")
                                : t("admin.branding.domain.pending")}
                            </Badge>
                          </Td>
                        </Tr>
                      )
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Flex>
    </Box>
  );
};
